import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, CreditCard, Lock } from 'lucide-react';
export function HeroSection() {
  return <section className="relative pt-2 pb-6 md:pt-20 md:pb-24 overflow-hidden bg-white" data-id="element-135">
      {/* Background gradient */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/10 blur-[100px] rounded-full pointer-events-none" data-id="element-136" />

      <div className="max-w-5xl mx-auto px-3 sm:px-6 relative z-10" data-id="element-137">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-center" data-id="element-138">
          {/* Brand badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-600 font-semibold text-[11px] md:text-sm mb-2 md:mb-8 border border-red-100" data-id="element-139">
            <span className="relative flex h-2 w-2 md:h-3 md:w-3" data-id="element-140">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-141"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 md:h-3 md:w-3 bg-red-500" data-id="element-142"></span>
            </span>
            ITERA PRO™
          </div>

          {/* Compact headline on mobile */}
          <h1 className="text-[17px] md:text-6xl font-extrabold tracking-tight text-gray-900 mb-1.5 md:mb-6 leading-[1.15] md:leading-snug px-2 md:px-0" data-id="element-143">
            Todo tu negocio de airbnb manager{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary" data-id="element-144">
              resuelto en un solo lugar
            </span>
          </h1>

          {/* Product Image - sized by viewport height on mobile so CTA always fits */}
          <div className="mb-2 md:mb-8 mx-auto flex items-center justify-center" style={{
          height: 'min(46vh, 360px)'
        }} data-id="element-145">
            <img src="https://cdn.magicpatterns.com/uploads/rjs2xbGFdBDcoU5EWHSHKS/ChatGPT_Image_May_19,_2026,_06_45_58_PM.png" alt="Megapack Operativo Airbnb Manager - 6 Tomos + 6 Bonos - Sistema Completo" width={768} height={768} loading="eager" decoding="async" fetchPriority="high" className="h-full w-auto max-w-full object-contain md:rounded-2xl" data-id="element-146" />
          </div>

          {/* Desktop-only subtitles */}
          <p className="hidden md:block text-lg text-gray-500 mb-4 max-w-2xl mx-auto leading-relaxed" data-id="element-147">
            El sistema N°1 para gestores de propiedades en Argentina — Sin
            propiedad propia — Con ingresos en USD
          </p>
          <p className="hidden md:block text-base text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-148">
            Contratos + operaciones + finanzas + automatización listos para
            usar. Sin improvisar. Sin empezar desde cero.
          </p>

          {/* CTA Button */}
          <motion.a href="#pricing" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="inline-flex items-center justify-center w-full sm:w-auto px-6 py-3.5 md:px-8 md:py-4 text-base md:text-lg font-bold text-white bg-gradient-to-r from-primary to-secondary rounded-xl md:rounded-2xl shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all" data-id="element-149">
            🔴 QUIERO MI MEGAPACK AHORA
          </motion.a>

          {/* Security trust line */}
          <div className="flex flex-wrap justify-center items-center gap-2.5 md:gap-8 mt-2.5 md:mt-6 text-[10px] md:text-sm text-gray-500 font-medium" data-id="element-150">
            <div className="flex items-center gap-1" data-id="element-151">
              <ShieldCheck className="w-3 h-3 md:w-4 md:h-4 text-green-500" data-id="element-152" />{' '}
              Garantía 7 días
            </div>
            <div className="flex items-center gap-1" data-id="element-153">
              <Lock className="w-3 h-3 md:w-4 md:h-4 text-gray-400" data-id="element-154" /> Pago
              seguro
            </div>
            <div className="flex items-center gap-1" data-id="element-155">
              <CreditCard className="w-3 h-3 md:w-4 md:h-4 text-blue-500" data-id="element-156" />{' '}
              Mercado Pago
            </div>
          </div>

          {/* Feature badges */}
          <div className="grid grid-cols-2 md:flex md:flex-wrap md:justify-center gap-2 md:gap-6 mt-5 md:mt-10 max-w-[400px] md:max-w-none mx-auto" data-id="element-157">
            {[{
            icon: '📄',
            text: '100% PDF'
          }, {
            icon: '⚡',
            text: 'Acceso en 5 min'
          }, {
            icon: '🇦🇷',
            text: 'Para Argentina'
          }, {
            icon: '💵',
            text: 'Ingresos en USD'
          }].map((badge, i) => <div key={i} className="flex items-center justify-center gap-1.5 md:gap-2 bg-surface px-2.5 md:px-4 py-1.5 md:py-2 rounded-full text-[11px] md:text-sm font-medium text-gray-700 border border-gray-100 shadow-sm" data-id="element-158">
                <span data-id="element-159">{badge.icon}</span>
                {badge.text}
              </div>)}
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-8 mt-6 md:mt-16 pt-5 md:pt-10 border-t border-gray-100" data-id="element-160">
            {[{
            value: '+300',
            label: 'managers confían'
          }, {
            value: '6',
            label: 'Tomos listos'
          }, {
            value: '6',
            label: 'Bonos incluidos'
          }, {
            value: '4.9/5',
            label: 'calificación ⭐'
          }].map((stat, i) => <div key={i} className="text-center" data-id="element-161">
                <div className="text-xl md:text-3xl font-extrabold text-gray-900 mb-0.5" data-id="element-162">
                  {stat.value}
                </div>
                <div className="text-[10px] md:text-sm text-gray-500 font-medium" data-id="element-163">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}